import React from 'react';

// 文本差异类型
export enum DiffType {
  EQUAL = 'equal',
  INSERT = 'insert',
  DELETE = 'delete',
  SUBSTITUTE = 'substitute'
}

// 差异片段接口
export interface DiffSegment {
  type: DiffType;
  text: string;
  originalText?: string; // 用于替换的情况
}

// 缩写映射表
const contractionMap: Record<string, string> = {
  "we're": "we are",
  "they're": "they are",
  "you're": "you are",
  "i'm": "i am",
  "he's": "he is",
  "she's": "she is",
  "it's": "it is",
  "we've": "we have",
  "they've": "they have",
  "you've": "you have",
  "i've": "i have",
  "we'll": "we will",
  "they'll": "they will",
  "you'll": "you will",
  "i'll": "i will",
  "he'll": "he will",
  "she'll": "she will",
  "it'll": "it will",
  "won't": "will not",
  "can't": "cannot",
  "don't": "do not",
  "doesn't": "does not",
  "didn't": "did not",
  "isn't": "is not",
  "aren't": "are not",
  "wasn't": "was not",
  "weren't": "were not",
  "haven't": "have not",
  "hasn't": "has not",
  "hadn't": "had not",
  "wouldn't": "would not",
  "couldn't": "could not",
  "shouldn't": "should not",
  "let's": "let us",
  "that's": "that is",
  "there's": "there is",
  "here's": "here is",
  "where's": "where is",
  "what's": "what is",
  "who's": "who is",
  "how's": "how is",
  "when's": "when is",
  "why's": "why is",
};

// 处理缩写的函数
function expandContractions(text: string): string {
  let expanded = text.toLowerCase();
  Object.entries(contractionMap).forEach(([contracted, expansion]) => {
    const regex = new RegExp(`\\b${contracted.replace("'", "\\'")}\\b`, 'gi');
    expanded = expanded.replace(regex, expansion);
  });
  return expanded;
}

// 分词函数 - 保留原始大小写用于显示
function tokenizeWithCase(text: string): { word: string; normalized: string }[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const words = text.match(/[\w']+|[^\w\s]+/g) || [];
  return words
    .filter(w => /\w/.test(w)) // 只保留包含字母数字的词
    .map(word => ({
      word: word,
      normalized: expandContractions(word.toLowerCase())
    }));
}

// 使用改进的Wagner-Fischer算法计算编辑距离和对齐
function computeAlignment(original: { word: string; normalized: string }[],
                         recorded: { word: string; normalized: string }[]): DiffSegment[] {
  const m = original.length;
  const n = recorded.length;

  // 创建DP表格
  const dp: number[][] = Array(m + 1).fill(null)
    .map(() => Array(n + 1).fill(0));

  // 操作类型表格：0=equal, 1=substitute, 2=delete, 3=insert
  const ops: number[][] = Array(m + 1).fill(null)
    .map(() => Array(n + 1).fill(0));

  // 初始化
  for (let i = 0; i <= m; i++) {
    dp[i][0] = i;
    if (i > 0) ops[i][0] = 2; // delete
  }
  for (let j = 0; j <= n; j++) {
    dp[0][j] = j;
    if (j > 0) ops[0][j] = 3; // insert
  }

  // 填充DP表格
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const origNorm = original[i-1].normalized;
      const recNorm = recorded[j-1].normalized;

      if (origNorm === recNorm) {
        dp[i][j] = dp[i-1][j-1];
        ops[i][j] = 0; // equal
      } else {
        const costs = [
          dp[i-1][j-1] + 1, // substitute
          dp[i-1][j] + 1,   // delete
          dp[i][j-1] + 1    // insert
        ];
        const minCost = Math.min(...costs);
        dp[i][j] = minCost;

        if (minCost === costs[0]) ops[i][j] = 1; // substitute
        else if (minCost === costs[1]) ops[i][j] = 2; // delete
        else ops[i][j] = 3; // insert
      }
    }
  }

  // 回溯找到最优对齐
  const result: DiffSegment[] = [];
  let i = m, j = n;

  while (i > 0 || j > 0) {
    if (i === 0) {
      // 只能插入
      result.unshift({
        type: DiffType.INSERT,
        text: recorded[j-1].word
      });
      j--;
    } else if (j === 0) {
      // 只能删除
      result.unshift({
        type: DiffType.DELETE,
        text: original[i-1].word
      });
      i--;
    } else {
      const op = ops[i][j];
      if (op === 0) {
        // 相等
        result.unshift({
          type: DiffType.EQUAL,
          text: original[i-1].word
        });
        i--;
        j--;
      } else if (op === 1) {
        // 替换
        result.unshift({
          type: DiffType.SUBSTITUTE,
          text: recorded[j-1].word,
          originalText: original[i-1].word
        });
        i--;
        j--;
      } else if (op === 2) {
        // 删除
        result.unshift({
          type: DiffType.DELETE,
          text: original[i-1].word
        });
        i--;
      } else {
        // 插入
        result.unshift({
          type: DiffType.INSERT,
          text: recorded[j-1].word
        });
        j--;
      }
    }
  }

  return result;
}

// 从保存的mistakes数据重构差异片段 - 支持详细位置信息
export function reconstructDiffFromMistakes(
  originalText: string,
  userTranscription: string,
  mistakes: {
    missing: string[];
    wrong: string[];
    extra: string[];
    // 带位置信息的详细数据（如果可用）
    detailedMistakes?: {
      missing: Array<{ word: string; originalPosition: number }>;
      wrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }>;
      extra: Array<{ word: string; userPosition: number }>;
    };
  }
): DiffSegment[] {
  // 如果有详细的位置信息，使用精确重构
  if (mistakes.detailedMistakes) {
    return reconstructFromDetailedMistakes(originalText, userTranscription, mistakes.detailedMistakes);
  }

  // 否则使用原算法（为了向后兼容）
  return calculateTextDiff(originalText, userTranscription);
}

// 使用详细位置信息进行精确重构
function reconstructFromDetailedMistakes(
  originalText: string,
  userTranscription: string,
  detailedMistakes: {
    missing: Array<{ word: string; originalPosition: number }>;
    wrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }>;
    extra: Array<{ word: string; userPosition: number }>;
  }
): DiffSegment[] {
  // 将原文和用户转录分词
  const originalTokens = tokenizeWithCase(originalText);
  const userTokens = tokenizeWithCase(userTranscription);

  // 创建位置映射
  const missingPositions = new Set(detailedMistakes.missing.map(m => m.originalPosition));
  const wrongPositions = new Map(detailedMistakes.wrong.map(w => [w.originalPosition, w]));
  const extraPositions = new Map(detailedMistakes.extra.map(e => [e.userPosition, e]));

  const segments: DiffSegment[] = [];
  let userIndex = 0;

  // 遍历原文tokens
  for (let origIndex = 0; origIndex < originalTokens.length; origIndex++) {
    const origToken = originalTokens[origIndex];

    // 检查是否为缺失的词
    if (missingPositions.has(origIndex)) {
      segments.push({
        type: DiffType.DELETE,
        text: origToken.word
      });
      continue;
    }

    // 检查是否为替换的词
    if (wrongPositions.has(origIndex)) {
      const wrongInfo = wrongPositions.get(origIndex)!;
      segments.push({
        type: DiffType.SUBSTITUTE,
        text: wrongInfo.userWord,
        originalText: wrongInfo.originalWord
      });
      userIndex++;
      continue;
    }

    // 检查是否有额外的词在当前位置
    while (extraPositions.has(userIndex)) {
      const extraInfo = extraPositions.get(userIndex)!;
      segments.push({
        type: DiffType.INSERT,
        text: extraInfo.word
      });
      userIndex++;
    }

    // 正常匹配的词
    if (userIndex < userTokens.length) {
      segments.push({
        type: DiffType.EQUAL,
        text: origToken.word
      });
      userIndex++;
    }
  }

  // 处理剩余的额外词
  while (userIndex < userTokens.length && extraPositions.has(userIndex)) {
    const extraInfo = extraPositions.get(userIndex)!;
    segments.push({
      type: DiffType.INSERT,
      text: extraInfo.word
    });
    userIndex++;
  }

  return segments;
}

// 更实用的方法：从ShadowingRecord创建用于显示的组件属性
export function createTextDiffPropsFromRecord(record: {
  sentenceText: string;
  userTranscription: string;
  accuracy: number;
  mistakes: {
    missing: string[];
    wrong: string[];
    extra: string[];
    // 可选的详细位置信息
    detailedMistakes?: {
      missing: Array<{ word: string; originalPosition: number }>;
      wrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }>;
      extra: Array<{ word: string; userPosition: number }>;
    };
  };
}) {
  // 使用保存的mistakes数据重构差异片段，确保显示与保存的数据完全一致
  const precomputedSegments = reconstructDiffFromMistakes(
    record.sentenceText,
    record.userTranscription,
    record.mistakes
  );

  return {
    originalText: record.sentenceText,
    userTranscription: record.userTranscription,
    accuracy: record.accuracy,
    precomputedSegments
  };
}

// 计算文本差异 - 改进版本
export function calculateTextDiff(original: string, recorded: string): DiffSegment[] {
  // 添加输入验证
  if (!original || !recorded || typeof original !== 'string' || typeof recorded !== 'string') {
    return [];
  }

  const originalTokens = tokenizeWithCase(original);
  const recordedTokens = tokenizeWithCase(recorded);

  return computeAlignment(originalTokens, recordedTokens);
}

// React组件显示差异
interface TextDiffHighlightProps {
  originalText: string;
  userTranscription: string;
  accuracy: number;
  // 新增：可以直接传入预计算的差异片段
  precomputedSegments?: DiffSegment[];
  renderScore?: (accuracy: number) => React.ReactNode;
}

export const TextDiffHighlight: React.FC<TextDiffHighlightProps> = ({
  originalText,
  userTranscription,
  accuracy,
  precomputedSegments,
  renderScore,
}) => {
  // 如果提供了预计算的片段，使用它们；否则重新计算
  const diffSegments = precomputedSegments || calculateTextDiff(originalText, userTranscription);

  return (
    <div className="space-y-2 sm:space-y-3">
      <div className="flex items-center gap-2 mb-1 sm:mb-3">
        <span className="text-lg font-semibold">Recognition Result:</span>
        {renderScore ? renderScore(accuracy) : <CircularProgress accuracy={accuracy} />}
      </div>

      {/* 如果没有差异片段，显示原始对比 */}
      {diffSegments.length === 0 ? (
        <div className="space-y-2">
          <div className="text-sm text-gray-600">Original:</div>
          <div className="text-lg bg-gray-50 p-2 rounded">{originalText}</div>
          <div className="text-sm text-gray-600">Your transcription:</div>
          <div className="text-lg bg-blue-50 p-2 rounded">{userTranscription}</div>
        </div>
      ) : (
        <div className="text-lg leading-relaxed flex flex-wrap items-baseline gap-1">
          {diffSegments.map((segment, index) => {
            // 添加空格分隔符
            const addSpace = index > 0 ? ' ' : '';

          // 渲染箭头和文本
          if (segment.type === DiffType.EQUAL) {
            return (
              <span key={index} className="inline-flex items-baseline">
                {addSpace}
                <span className="text-gray-700">{segment.text}</span>
              </span>
            );
          } else if (segment.type === DiffType.DELETE) {
            return (
              <span key={index} className="inline-flex items-baseline gap-1">
                {addSpace}
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-sm font-medium text-red-600">
                  {segment.text}
                </span>
              </span>
            );
          } else if (segment.type === DiffType.INSERT) {
            return (
              <span key={index} className="inline-flex items-baseline">
                {addSpace}
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-sm font-medium text-blue-600">
                  {segment.text}
                </span>
              </span>
            );
          } else if (segment.type === DiffType.SUBSTITUTE) {
            return (
              <span key={index} className="inline-flex items-baseline gap-1">
                {addSpace}
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-sm font-medium text-blue-600">{segment.originalText}</span>
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-sm font-medium text-red-600">{segment.text}</span>
              </span>
            );
          }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

// 圆形进度条组件
const CircularProgress: React.FC<{ accuracy: number }> = ({ accuracy }) => {
  const radius = 30;
  const strokeWidth = 4;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (accuracy / 100) * circumference;

  // 根据准确度选择颜色
  const getColor = (acc: number) => {
    if (acc >= 80) return '#10b981'; // green-500
    if (acc >= 60) return '#f59e0b'; // amber-500
    return '#ef4444'; // red-500
  };

  const color = getColor(accuracy);

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        height={radius * 2}
        width={radius * 2}
        className="transform -rotate-90"
      >
        {/* 背景圆 */}
        <circle
          stroke="#e5e7eb"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* 进度圆 */}
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease' }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <span
        className="absolute text-sm font-semibold"
        style={{ color }}
      >
        {Math.round(accuracy)}%
      </span>
    </div>
  );
};
